import { catalogServiceClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type { CatalogEnvelope } from '../types/catalog-service.api.types';
import type { ScopeRowsPage, ScopeRowsRequest } from '../types/new-batch.api.types';
import type {
  AlreadyCuratedBySelectionAccepted,
  AlreadyCuratedBySelectionRequest,
  AlreadyCuratedBySelectionStatus,
  CatalogOperationsAccepted,
  CatalogOperationsTrigger,
  TriggerFiltersRead,
  TriggerOptionsRead,
} from '../types/runs.api.types';
import { toSearchParams, unwrap } from '../utils/envelope';

/**
 * What the New Run form may offer: connectors (use cases, default flow, field
 * vocabulary), active flows, ingest modes and the `maxProducts` rules. Omit
 * `connectorCode` to get every connector in one read.
 */
export async function getTriggerOptions(connectorCode?: string): Promise<TriggerOptionsRead> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.triggerOptions(), {
      searchParams: toSearchParams({ connectorCode }),
    })
    .json<CatalogEnvelope<TriggerOptionsRead>>();
  return unwrap(body);
}

/** The predefined trigger filter keys for a connector, their stored values, and the dynamic columns. */
export async function getTriggerFilters(connectorCode: string): Promise<TriggerFiltersRead> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.triggerFilters(), {
      searchParams: toSearchParams({ connectorCode }),
    })
    .json<CatalogEnvelope<TriggerFiltersRead>>();
  return unwrap(body);
}

/**
 * Start a run now. 202 → committed run (`jobId`) plus warnings; 409 lane busy; 422 invalid body.
 * A later start is a one-off schedule instead (`createSchedule` with `once`, api-changes §74).
 */
export async function triggerRun(
  payload: CatalogOperationsTrigger,
): Promise<CatalogOperationsAccepted> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.operations.trigger(), { json: payload })
    .json<CatalogEnvelope<CatalogOperationsAccepted>>();
  return unwrap(body);
}

/**
 * "Check what's already curated" (optional, never blocks Start run): submits the
 * New Run form's confirmed whole-data scope as an async breakdown job. 202 answers
 * a job id to poll; a Temporal outage closes the job failed rather than hanging.
 * Legacy route, no envelope.
 */
export async function submitAlreadyCuratedCheck(
  payload: AlreadyCuratedBySelectionRequest,
): Promise<AlreadyCuratedBySelectionAccepted> {
  return catalogServiceClient
    .post(endpoints.catalogService.curation.legacyBySelection(), { json: payload })
    .json<AlreadyCuratedBySelectionAccepted>();
}

/** Polls a submitted breakdown job; terminal once `status` is succeeded/failed. No envelope. */
export async function getAlreadyCuratedCheckStatus(
  jobId: string,
): Promise<AlreadyCuratedBySelectionStatus> {
  return catalogServiceClient
    .get(endpoints.catalogService.curation.legacyBySelectionStatus(jobId))
    .json<AlreadyCuratedBySelectionStatus>();
}

/** Pages one Scope bucket's rows (clean | flagged | net_new | item_issues) for the confirmed scope. */
export async function getAlreadyCuratedRows(payload: ScopeRowsRequest): Promise<ScopeRowsPage> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.curation.alreadyCuratedRows(), { json: payload })
    .json<CatalogEnvelope<ScopeRowsPage>>();
  return unwrap(body);
}
